import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../../../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../../../../shared/pipes/translate.pipe';

/**
 * Formly field type for checkbox group selection
 * Supports loading options dynamically and displaying them as checkboxes
 *
 * Props:
 * - label: string - Field label
 * - description: string - Helper text
 * - required: boolean - Whether selection is required
 * - options: Array<{value: any, label: string, description?: string}> - Options to display
 * - loading: boolean - Whether options are loading
 * - emptyMessage: string - Message when no options available
 * - multiple: boolean - Allow multiple selections (default: true)
 */
@Component({
  selector: 'app-checkbox-group-field',
  imports: [ReactiveFormsModule, ButtonComponent, IconComponent, SpinnerComponent, TranslatePipe],
  templateUrl: './checkbox-group-field.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './checkbox-group-field.component.css',
})
export class CheckboxGroupFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  ngOnInit(): void {
    // Apply default(s) when the control has no value yet (new form interaction).
    // Multiple defaults are supported for checkbox groups.
    const currentValue = this.formControl.value;
    if (
      currentValue === null ||
      currentValue === undefined ||
      (Array.isArray(currentValue) && currentValue.length === 0)
    ) {
      const defaults = this.checkboxOptions.filter((o) => o.isDefault).map((o) => o.value);
      if (defaults.length > 0) {
        this.formControl.setValue(defaults);
      }
    }
  }
  /**
   * Check if a value is selected
   */
  isSelected(value: any): boolean {
    const currentValue = this.formControl.value;

    if (Array.isArray(currentValue)) {
      return currentValue.includes(value);
    }

    return currentValue === value;
  }

  /**
   * Toggle a checkbox value
   */
  toggleValue(value: any, event: Event): void {
    event.preventDefault();

    const currentValue = this.formControl.value || [];
    const multiple = this.props['multiple'] !== false; // Default to true

    if (multiple) {
      // Multi-select mode
      const newValue = Array.isArray(currentValue) ? [...currentValue] : [];
      const index = newValue.indexOf(value);

      if (index > -1) {
        newValue.splice(index, 1);
      } else {
        newValue.push(value);
      }

      this.formControl.setValue(newValue);
    } else {
      // Single-select mode
      this.formControl.setValue(this.isSelected(value) ? null : value);
    }
  }

  /**
   * Get available options from props
   */
  get checkboxOptions(): {
    value: any;
    label: string;
    description?: string;
    isDefault?: boolean;
  }[] {
    const opts = this.props['options'] as any;
    if (Array.isArray(opts)) {
      return opts;
    }
    return [];
  }

  /**
   * Check if options are loading
   */
  get isLoading(): boolean {
    return this.props['loading'] || false;
  }

  /**
   * Get empty state message
   */
  get emptyStateMessage(): string {
    return this.props['emptyMessage'] || 'No options available';
  }

  /**
   * Get action buttons configuration
   */
  get actionButtons(): {
    icon: string;
    label: string;
    action: string;
    showWhen?: 'always' | 'selected';
  }[] {
    return this.props['actionButtons'] || [];
  }

  /**
   * Check if a button should be shown for a given option
   */
  shouldShowButton(
    button: { icon: string; label: string; action: string; showWhen?: 'always' | 'selected' },
    optionValue: any,
  ): boolean {
    if (button.showWhen === 'always') {
      return true;
    }
    // Default to 'selected' if not specified
    return this.isSelected(optionValue);
  }

  /**
   * Handle action button click
   */
  handleActionClick(
    button: { icon: string; label: string; action: string },
    option: { value: any; label: string; description?: string },
  ): void {
    const callback = this.props['onActionClick'];
    if (typeof callback === 'function') {
      callback(button.action, option);
    }
  }
}
